import { Info } from "lucide-react";
import { formatCount } from "@/lib/format";
import { useTranslation } from "react-i18next";

import { Section, type DiagnosticTabProps } from "../../DiagnosticSection";
import { CardNotes } from "../../primitives/CardNotes";
import { documentVolumes, type DocumentFamilyVolume } from "../../diagnosticData";
import {
  DA_DOC_TYPE_NAMES,
  digitalAccessFrame,
  volumesByType,
  type DaTypeVolume,
} from "../../selectors/digitalAccess";
import { NO_VALUE } from "../../sectionState";
import type { T } from "./i18nTypes";

const FAMILY_LABEL = (t: T): Record<string, string> => ({
  // A unidade entra no rótulo porque ela é o número que vira dinheiro no Ato 5.
  // "Vendas 7.220" lê como 7.220 pedidos, e a `VBAP` conta **item de linha** —
  // Sales, Invoice, Purchase, Financial e Material contam por item; as outras
  // quatro, por documento (Service Use Description v.10-2025).
  sales: t("Sales order line items"),
  purchase: t("Purchase order line items"),
  invoice: t("Billing document line items"),
  manufacturing: t("Production orders"),
  quality: t("Quality notifications"),
  service_maintenance: t("Maintenance orders"),
  time_management: t("Time sheet records"),
  financial: t("Accounting document line items"),
  material: t("Material document line items"),
  // #939: a nota fiscal brasileira, que o desenho pede por nome. Não é a
  // família `invoice` — aquela é `VBRP`, documento de faturamento.
  nfe: t("NF-e issued"),
});

/**
 * Altura util da barra, em pixel: o `h-24` da faixa menos o rotulo e o `gap`.
 */
const BAR_AREA_PX = 76;

/**
 * Quantos documentos o cliente cria por período (#684).
 *
 * O card de volumetria que existia antes conta linha de tabela, que é
 * **estoque**: um cliente com quinze anos de histórico aparece igual a um com
 * dois se acumularam o mesmo total. Aqui é **fluxo**, que é o que o roteiro
 * comercial pede no Ato 1 e o que o Digital Access cobra — a métrica do
 * contrato é anual.
 *
 * Duas famílias não têm série mensal, e isso aparece escrito em vez de virar
 * doze zeros: `BSEG` e `MSEG` só têm ano fiscal, sem campo de mês nenhum. Doze
 * zeros ali se leriam como ausência de documento, e é ausência de campo.
 */
export function DocumentVolumesCard({ sections, snapshots, validation }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const volumes = documentVolumes(sections);
  const labels = FAMILY_LABEL(t);
  /*
    A lista sai da contagem do Digital Access quando ela existe (#1713, cartão 5.2).

    O Marco decidiu em 22/10/2026 que o card usa a contagem do quadro de Digital
    Access: o mesmo ano, os nove tipos e a NF-e numa linha à parte. O
    `/document-volumes` tem teto de leitura, e na AL5 o card mostrava "600.000+"
    itens contábeis em três anos com o quadro logo abaixo dizendo 6,08 milhões só
    em 2025. Sem os itens do Digital Access (execução antiga, ECC antes da #1525),
    a lista continua a das famílias do `/document-volumes`.
  */
  const frame = digitalAccessFrame(sections, validation);
  const byType = frame ? volumesByType(frame) : null;
  const nfe = (volumes?.families ?? []).find((f) => f.family === "nfe");
  const nfeInYear =
    frame?.basisYear && nfe?.collected ? nfe.byYear[frame.basisYear] : undefined;

  const monthly = (volumes?.families ?? []).filter(
    (f) => f.collected && f.monthlySupported,
  );
  const months = Array.from(
    new Set(monthly.flatMap((f) => Object.keys(f.byMonth))),
  ).sort();
  const totalByMonth = months.map((month) => ({
    month,
    total: monthly.reduce((sum, f) => sum + (f.byMonth[month] ?? 0), 0),
  }));
  const peak = Math.max(1, ...totalByMonth.map((m) => m.total));
  // Série inteira zerada não vira gráfico. Sem barra nenhuma sobra uma fileira
  // de rótulos soltos, que parece defeito de render — e o que houve foi
  // medição. O lugar vazio se declara, como no resto da tela.
  const hasFlow = totalByMonth.some((m) => m.total > 0);
  // ⚠️ E o vazio da série mensal **não é** o vazio do card. A série cobre os
  // últimos doze meses; os totais cobrem a janela inteira do projeto. Num
  // sistema cujo fluxo terminou anos atrás, a primeira é toda zero e os
  // segundos não são — e a tela chegou a dizer "nenhum documento foi criado"
  // com 319.302 lançamentos logo abaixo.
  const hasAnyFlow = (volumes?.families ?? []).some(
    (family) => family.collected && (family.total ?? 0) > 0,
  );
  /*
    Tentada e recusada não é não tentada.

    Até 02/10/2026 a seção virava `not_collected` quando nenhuma família
    respondia, e o card dizia "nenhuma execução concluída produziu este bloco"
    sobre um bloco que a execução produziu: foram dez chamadas, e as nove
    famílias voltaram `TABLE_NOT_FOUND`. O builder passou a contar as
    **tentadas** e a carregar o `reason` de cada uma, como o `data_volumes` já
    fazia; o que falta é o card dizer o que recebeu.

    Um motivo só, e não nove: elas falham pela mesma causa, e repetir a mesma
    linha nove vezes esconderia o caso em que são duas causas diferentes — que
    é o que a contagem ao lado do motivo revela.
  */
  const refused = (volumes?.families ?? []).filter((family) => !family.collected);
  const refusalReason = refused.find((family) => family.reason)?.reason;
  /*
    Recusa **parcial** também fala. Exigir que todas tivessem sido recusadas
    deixava 9 de 10 mudas, e desde que a seção passou a nascer `ok` o cabeçalho
    do card afirma que o bloco foi coletado enquanto 90% dele não respondeu. A
    contagem na frase é que carrega a proporção.
  */
  const hasRefused = refused.length > 0;

  /*
    As ressalvas do card vão para o ícone do canto (#682), no mesmo padrão da
    vitalidade e do catálogo WRICEF. As três falam de estados que um cliente em
    operação não tem: janela cortada pelo teto do connector, período inteiro
    sem documento e fluxo recente vazio com janela cheia.

    A anotação de "só anual" **não** entra aqui: ela qualifica um número
    específico da lista, e longe dele não diria de quem está falando.
  */
  /*
    Período inteiro sem documento é "não medido", nunca zero (#1525, cartão 5.6).

    A TORA está ligada ao ambiente de Qualidade, sem documento lançado de 2024 a
    2026, e o Marco decidiu em 22/10/2026 que o Volume real dela sai como "não
    medido". Nove zeros se leriam como um cliente que não fatura; o que houve foi
    um ambiente sem movimento, que não diz o volume do negócio.
  */
  // Só o medido e vazio: família recusada ou item sem número já se dizem como tal
  // na linha, com o travessão.
  const notMeasured = byType
    ? !byType.some((type) => (type.total ?? 0) > 0 || type.uncounted) && !((nfeInYear ?? 0) > 0)
    : !hasAnyFlow && !(volumes?.families ?? []).some((family) => !family.collected);

  const notes: string[] = [];
  // As duas notas falam da janela do `/document-volumes`, que só a lista antiga mostra.
  if (volumes && !byType && !notMeasured) {
    if (volumes.cappedYears != null && volumes.requestedYears != null) {
      notes.push(
        t("The project window asked for {{requested}} years; this reading covers {{covered}}.", {
          requested: volumes.requestedYears,
          covered: volumes.cappedYears,
        }),
      );
    }
    if (totalByMonth.length > 0 && !hasFlow) {
      notes.push(
        t(
          "No movement in the last months, and the flow of the window is below. The two are measurements of different periods.",
        ),
      );
    }
  }

  return (
    <Section
      sections={sections}
      name="document_volumes"
      title={t("Real volume of the business processes")}
      subtitle={t("Documents measured in the productive environment")}
      insight={t("These volumes feed the Digital Access sizing in Investment.")}
      actions={<CardNotes testId="document-volumes-notes" notes={notes} />}
      snapshots={snapshots}
      showsSampling={false}
    >
      {volumes && (
        <div data-testid="document-volumes">
          {hasRefused && !byType && (
            <p
              data-testid="document-volumes-refused"
              className="flex items-start gap-2 rounded-md border border-[#e8ebf0] bg-[#f7f9fc] px-3 py-2 text-[11px] leading-snug text-[#596b85]"
            >
              <Info aria-hidden className="mt-px h-3.5 w-3.5 shrink-0 text-[#7b8799]" />
              <span>
                {/* O enunciado não fala de pedido, porque os dois casos
                    chegam aqui: a origem tentou e recusou, ou a estratégia
                    dela sabe que não vale tentar e não perguntou. Qual dos
                    dois foi está no motivo de cada família. */}
                {refusalReason
                  ? t("{{count}} document families have no period count: {{reason}}", {
                      count: refused.length,
                      reason: refusalReason,
                    })
                  : t("{{count}} document families have no period count.", {
                      count: refused.length,
                    })}
              </span>
            </p>
          )}
          {notMeasured && (
            <p
              data-testid="document-volumes-not-measured"
              className="flex items-start gap-2 rounded-md border border-[#e8ebf0] bg-[#f7f9fc] px-3 py-2 text-[11px] leading-snug text-[#596b85]"
            >
              <Info aria-hidden className="mt-px h-3.5 w-3.5 shrink-0 text-[#7b8799]" />
              <span>
                {t(
                  "Not measured: no document was created in the period read. A system without movement does not show the volume of the business.",
                )}
              </span>
            </p>
          )}
          {!notMeasured && totalByMonth.length > 0 && hasFlow && (
            <div className="mb-4" data-testid="document-volumes-monthly">
              <div className="flex h-24 items-end gap-1">
                {totalByMonth.map(({ month, total }) => (
                  <div
                    key={month}
                    className="flex h-full flex-1 flex-col items-center justify-end gap-1"
                  >
                    {/*
                      Altura em pixel, e não em percentual. Percentual resolve
                      contra a altura do pai, e a coluna só embrulhava barra e
                      rótulo, sem altura própria: os 93% de um mês cheio caíam
                      para zero e sobrava a fileira de rótulos soltos, com o
                      dado inteiro presente no payload.
                    */}
                    <div
                      className="w-full rounded-sm bg-[#6d8fc4]"
                      style={{ height: `${Math.max(2, Math.round((total / peak) * BAR_AREA_PX))}px` }}
                      title={`${month}: ${formatCount(total)}`}
                    />
                    {/*
                      `MM/AA`, e não só o mês: a janela de doze meses sempre
                      cruza a virada do ano, e "10 11 12 01" não diz de que ano
                      é cada um.
                    */}
                    <span className="text-[9px] text-[#7b8799]">
                      {`${month.slice(5)}/${month.slice(2, 4)}`}
                    </span>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-[#596b85]">
                {t("Documents created per month, across the families with a date axis.")}
              </p>
            </div>
          )}

          {/*
            Barra, e não coluna de número. "484.761" e "63.592" na mesma coluna
            só se comparam lendo dígito por dígito; a barra faz a diferença
            entre os processos saltar, que é o que o desenho aprovado mostra.

            Ordem por volume, com quem não respondeu no fim. Família sem número
            não tem lugar na ordem, e pôr zero para ordená-la inventaria a
            medição que faltou.
          */}
          {!notMeasured && byType && frame && (
            <TypeVolumes types={byType} year={frame.basisYear} nfe={nfe} nfeInYear={nfeInYear} />
          )}
          {!notMeasured && !byType && (
            <div className="flex flex-col gap-1.5">
              {orderedFamilies(volumes.families ?? []).map((family) => (
                <div
                  key={family.family}
                  data-testid={`document-family-${family.family}`}
                  className="flex items-center gap-3 text-[11px]"
                >
                  {/* Largura para o rótulo mais longo ("Itens de documento de
                      faturamento"): com `w-36` os nomes que dizem a unidade
                      saíam cortados, e cortar justamente a parte que distingue
                      item de documento anula o motivo de tê-los alongado. */}
                  <div className="w-52 shrink-0 truncate text-[#08254c]" title={labels[family.family] ?? family.family}>
                    {labels[family.family] ?? family.family}
                  </div>
                  <div className="h-4 min-w-0 flex-1 rounded-sm bg-[#f2f4f7]">
                    {family.collected && family.total != null && familyPeak(volumes.families) > 0 && (
                      <div
                        data-testid="family-bar"
                        className="h-full rounded-sm bg-[#6d8fc4]"
                        style={{
                          width: `${Math.max(1, (family.total / familyPeak(volumes.families)) * 100)}%`,
                        }}
                      />
                    )}
                  </div>
                  <div className="w-24 shrink-0 text-right font-mono text-[#08254c]">
                    {familyTotal(family)}
                  </div>
                  <div className="w-40 shrink-0 text-[10px] text-[#7b8799]">
                    {familyNote(family, t)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

/**
 * Os nove tipos do Digital Access no ano do quadro, com a NF-e numa linha à parte
 * (#1713, cartão 5.2).
 *
 * O nome do tipo é o da SAP, o mesmo do quadro, para a pessoa achar a linha de um
 * no outro. O `+` vai onde o quadro também põe: item que cortou no teto ou que
 * ficou sem número. A NF-e não é item do Digital Access (EF-FUNC-005 RF-04), e
 * por isso fica fora da lista, mas no mesmo ano.
 */
function TypeVolumes({
  types,
  year,
  nfe,
  nfeInYear,
}: {
  types: DaTypeVolume[];
  year: string | undefined;
  nfe: DocumentFamilyVolume | undefined;
  nfeInYear: number | undefined;
}) {
  const { t } = useTranslation();
  const ordered = [
    ...types
      .filter((type) => type.total !== undefined)
      .sort((a, b) => (b.total ?? 0) - (a.total ?? 0)),
    ...types.filter((type) => type.total === undefined),
  ];
  const peak = Math.max(0, ...types.map((type) => type.total ?? 0));
  const nfeAmount =
    nfeInYear === undefined ? NO_VALUE : `${formatCount(nfeInYear)}${nfe?.totalIsFloor ? "+" : ""}`;

  return (
    <>
      {year && (
        <p data-testid="document-volumes-basis" className="mb-2 text-[11px] text-[#596b85]">
          {t("Documents created in {{year}}, by the same count as the Digital Access table.", {
            year,
          })}
        </p>
      )}
      <div className="flex flex-col gap-1.5">
        {ordered.map((type) => (
          <div
            key={type.docType}
            data-testid={`document-family-${type.docType}`}
            className="flex items-center gap-3 text-[11px]"
          >
            <div className="w-52 shrink-0 truncate text-[#08254c]" title={DA_DOC_TYPE_NAMES[type.docType]}>
              {DA_DOC_TYPE_NAMES[type.docType]}
            </div>
            <div className="h-4 min-w-0 flex-1 rounded-sm bg-[#f2f4f7]">
              {(type.total ?? 0) > 0 && (
                <div
                  data-testid="family-bar"
                  className="h-full rounded-sm bg-[#6d8fc4]"
                  style={{ width: `${Math.max(1, ((type.total ?? 0) / peak) * 100)}%` }}
                />
              )}
            </div>
            <div data-testid="document-family-value" className="w-24 shrink-0 text-right font-mono text-[#08254c]">
              {type.total === undefined
                ? NO_VALUE
                : `${formatCount(type.total)}${type.cut || type.uncounted ? "+" : ""}`}
            </div>
            <div className="w-40 shrink-0 text-[10px] text-[#7b8799]">{typeNote(type, t)}</div>
          </div>
        ))}
      </div>
      <div
        data-testid="document-volumes-nfe"
        className="mt-2 flex items-center gap-3 border-t border-[#eef1f5] pt-2 text-[11px]"
      >
        <div className="w-52 shrink-0 truncate text-[#08254c]">{t("NF-e issued")}</div>
        <div className="min-w-0 flex-1" />
        <div className="w-24 shrink-0 text-right font-mono text-[#08254c]">{nfeAmount}</div>
        <div className="w-40 shrink-0 text-[10px] text-[#7b8799]">
          {nfeInYear === undefined && nfe ? familyNote(nfe, t) : t("outside Digital Access")}
        </div>
      </div>
    </>
  );
}

function typeNote(type: DaTypeVolume, t: T): string {
  if (type.notApplicable) return t("Not applicable");
  if (type.total === undefined) return t("not counted");
  if (type.cut) return t("at least this many: the count hit its ceiling");
  if (type.uncounted) return t("at least this many: part of the items was not counted");
  return "";
}

/**
 * Da maior para a menor, com quem não respondeu no fim.
 *
 * Família sem número não entra na ordem por volume: ela não tem volume. Tratá-la
 * como zero a colocaria junto das que mediram zero, que é outra afirmação.
 */
function orderedFamilies(families: DocumentFamilyVolume[]): DocumentFamilyVolume[] {
  const withNumber = families.filter((f) => f.collected && f.total != null);
  const withoutNumber = families.filter((f) => !(f.collected && f.total != null));
  return [
    ...[...withNumber].sort((a, b) => (b.total ?? 0) - (a.total ?? 0)),
    ...withoutNumber,
  ];
}

/** Maior volume medido, que é a régua das barras. */
function familyPeak(families: DocumentFamilyVolume[]): number {
  return Math.max(
    0,
    ...families.filter((f) => f.collected && f.total != null).map((f) => f.total ?? 0),
  );
}

function familyTotal(family: DocumentFamilyVolume): string {
  // Piso não é total. A varredura bateu o teto de linha ou o orçamento, e o
  // número que saiu é o mínimo que existe — o `+` é o que separa os dois.
  if (family.collected && family.totalIsFloor) {
    return `${formatCount(family.total ?? 0)}+`;
  }
  if (!family.collected) {
    // O acumulado da tabela, quando foi só o que a origem deu. Ele ocupa a
    // coluna do número porque é um número de verdade; a nota ao lado é que diz
    // que ele não é do período. Sem barra: medi-lo contra o pico do fluxo
    // compararia duas grandezas.
    return family.tableTotal != null ? formatCount(family.tableTotal) : NO_VALUE;
  }
  return formatCount(family.total ?? 0);
}

/**
 * A nota por família diz o que o número é, e nunca o que ele não é.
 *
 * Família sem eixo de mês declara isso; família que não respondeu declara que
 * não respondeu. As duas coisas viram travessão na coluna do número, e sem a
 * nota elas ficariam indistinguíveis.
 */
function familyNote(family: DocumentFamilyVolume, t: T): string {
  if (family.collected && family.totalIsFloor) {
    return t("at least this many: the count hit its ceiling");
  }
  if (!family.collected) {
    return family.tableTotal != null
      ? t("whole table, no period cut")
      : t("source did not respond");
  }
  if (!family.monthlySupported) return t("yearly only: this table has no month field");
  return "";
}
